'use client';

// ---------------------------------------------------------------------------
// UpdateBanner — Professional 3-phase software update experience.
// Phase 1: "Update available" with "Update to Latest" button
// Phase 2: "Updating..." with progress bar (pre-caching assets)
// Phase 3: "Software updated! Reload to experience"
// ---------------------------------------------------------------------------

import { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useOfflineStore } from '@/core/stores/offline.store';
import { precacheAssets } from '@/core/lib/precache-assets';
import { useTranslation } from '@/core/i18n';
import { CURRENT_VERSION } from '@/core/config/changelog';

export function UpdateBanner({ idPrefix = 'sw' }: { idPrefix?: string } = {}) {
  const phase = useOfflineStore((s) => s.swUpdatePhase);
  const version = useOfflineStore((s) => s.swUpdateVersion);
  const progress = useOfflineStore((s) => s.swPrecacheProgress);
  const setPhase = useOfflineStore((s) => s.setSwUpdatePhase);
  const setProgress = useOfflineStore((s) => s.setSwPrecacheProgress);
  const [dismissed, setDismissed] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local UI state on phase change
    if (phase === 'idle') setDismissed(false);
  }, [phase]);

  const handleUpdate = useCallback(async () => {
    setPhase('updating');
    setProgress({ total: 0, loaded: 0 });

    await precacheAssets((p) => {
      setProgress({ total: p.total, loaded: p.loaded });
      if (p.phase === 'complete' || p.phase === 'error') {
        setPhase('ready');
      }
    });
  }, [setPhase, setProgress]);

  const handleReload = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hms-just-updated', CURRENT_VERSION);
      window.location.reload();
    }
  }, []);

  if (phase === 'idle' || dismissed) return null;

  const displayVersion = version
    ? version.replace(/^hotelstack-v/, 'v')
    : `v${CURRENT_VERSION}`;

  const progressPct =
    progress && progress.total > 0
      ? Math.round((progress.loaded / progress.total) * 100)
      : 0;

  return (
    <div
      id={`${idPrefix}-update-banner`}
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 left-1/2 z-[120] -translate-x-1/2 transform',
        'flex items-center gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur',
        'max-w-md w-[calc(100vw-2rem)]',
        phase === 'ready'
          ? 'border-hms-green/40 bg-hms-card/95'
          : 'border-hms-gold/40 bg-hms-card/95',
      )}
    >
      <div
        id={`${idPrefix}-update-icon`}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          phase === 'ready' ? 'bg-hms-green/15' : 'bg-hms-gold/15',
        )}
      >
        {phase === 'available' && (
          <DownloadIcon className="h-4 w-4 text-hms-gold" />
        )}
        {phase === 'updating' && (
          <SpinnerIcon className="h-4 w-4 text-hms-gold animate-spin" />
        )}
        {phase === 'ready' && (
          <CheckCircleIcon className="h-4 w-4 text-hms-green" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        {phase === 'available' && (
          <>
            <p id={`${idPrefix}-update-title`} className="text-sm font-semibold text-hms-text">
              {t('shared.updateBanner.updateAvailable')} {displayVersion}
            </p>
            <p id={`${idPrefix}-update-subtitle`} className="text-[12px] leading-relaxed text-hms-textSub">
              {t('shared.updateBanner.newVersionReady')}
            </p>
          </>
        )}
        {phase === 'updating' && (
          <>
            <p id={`${idPrefix}-update-title`} className="text-sm font-semibold text-hms-text">
              {t('shared.updateBanner.updating')}
            </p>
            <div
              id={`${idPrefix}-update-progress`}
              className="mt-1.5 h-1.5 w-full rounded-full bg-hms-border overflow-hidden"
            >
              <div
                className="h-full rounded-full bg-hms-gold transition-all duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </>
        )}
        {phase === 'ready' && (
          <>
            <p id={`${idPrefix}-update-title`} className="text-sm font-semibold text-hms-text">
              {t('shared.updateBanner.softwareUpdated')}
            </p>
            <p id={`${idPrefix}-update-subtitle`} className="text-[12px] leading-relaxed text-hms-textSub">
              {t('shared.updateBanner.reloadToExperience')}
            </p>
          </>
        )}
      </div>

      {phase === 'available' && (
        <button
          id={`${idPrefix}-update-btn`}
          type="button"
          onClick={handleUpdate}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg bg-hms-gold px-3 py-1.5',
            'text-xs font-semibold text-hms-bg hover:bg-hms-goldLt transition-colors whitespace-nowrap',
          )}
        >
          <DownloadIcon className="h-3.5 w-3.5" />
          {t('shared.updateBanner.updateToLatest')}
        </button>
      )}

      {phase === 'ready' && (
        <button
          id={`${idPrefix}-update-reload-btn`}
          type="button"
          onClick={handleReload}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg bg-hms-green px-3 py-1.5',
            'text-xs font-semibold text-white hover:bg-hms-green/90 transition-colors whitespace-nowrap',
          )}
        >
          {t('shared.updateBanner.reloadNow')}
        </button>
      )}

      {phase !== 'updating' && (
        <button
          id={`${idPrefix}-update-dismiss-btn`}
          type="button"
          onClick={() => setDismissed(true)}
          className="rounded-md p-1 text-hms-textDim hover:text-hms-text hover:bg-hms-border/40"
          aria-label={t('shared.updateBanner.dismiss')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

export default UpdateBanner;
